import React, { useState } from 'react'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  EndAnnualInsuranceRequest,
  InsertCoverageProviderForMonthRequest,
  endAnnualInsurance,
  insertCoverageProviderForMonth,
} from 'core'

import { Alert, DefaultDatePickerV2, DefaultInputV2, DefaultRadioGroupV2, Modal, Text } from 'common/components'
import { formatShortMonthYear, isFutureDate } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

type EndInsuranceCoverageModalProps = {
  planPeriodId: string
  editInsurance: EditInsurance
  visible?: boolean
  onSaved?: () => void
  onCancel?: () => void
}

export type EditInsurance = {
  startDate: string
  coveredByAnIndividualHealthInsurancePolicy?: boolean
  policyName?: string
  providerName?: string
}

type InitialForm = {
  endDate?: string
  coveredByAnIndividualHealthInsurancePolicy?: boolean
  coveredByAnotherIndividualHealthInsurancePolicy?: boolean
  providerName?: string
  policyName?: string
  providerStartDate?: string
}

const EditInsuranceCoverageModal = ({
  planPeriodId,
  editInsurance,
  visible,
  onCancel = () => {},
  onSaved = () => {},
}: EndInsuranceCoverageModalProps) => {
  const [isValidForm, setIsValidForm] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<InitialForm>({
    coveredByAnIndividualHealthInsurancePolicy: editInsurance.coveredByAnIndividualHealthInsurancePolicy,
  })

  const { sendCommand: sendEndAnnualInsurance, isLoading: isEndAnnualInsuranceLoading } = useCommandRequestV2(
    (request: EndAnnualInsuranceRequest) => endAnnualInsurance(request),
  )

  const { sendCommand: sendConfirmCoverageProviderForMonth, isLoading: isSendConfirmCoverageProviderForMonthLoading } =
    useCommandRequestV2((request: InsertCoverageProviderForMonthRequest) => insertCoverageProviderForMonth(request))

  const onFormSubmit = async () => {
    const result =
      initialValues.coveredByAnIndividualHealthInsurancePolicy && editInsurance.providerName && editInsurance.policyName
        ? await sendConfirmCoverageProviderForMonth({
            startDate: editInsurance.startDate,
            providerName: editInsurance.providerName,
            policyName: editInsurance.policyName,
            attestation: true,
          })
        : await sendEndAnnualInsurance({
            planPeriodId,
            endDate: initialValues.endDate,
            insuranceRequest: initialValues.coveredByAnotherIndividualHealthInsurancePolicy
              ? {
                  policyName: initialValues.policyName,
                  providerName: initialValues.providerName,
                  startDate: initialValues.providerStartDate,
                }
              : null,
          })

    if (result.completed) {
      onSaved()
    }
  }

  return (
    <>
      <Modal
        visible={visible}
        title={`Attest to your insurance coverage for ${formatShortMonthYear(editInsurance.startDate)}`}
        okType={'primary'}
        okText={'Submit'}
        onOk={onFormSubmit}
        okButtonProps={{
          disabled: !isValidForm || isEndAnnualInsuranceLoading || isSendConfirmCoverageProviderForMonthLoading,
          loading: isEndAnnualInsuranceLoading || isSendConfirmCoverageProviderForMonthLoading,
        }}
        onCancel={onCancel}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={formValidator}
          onSubmit={() => {}}
          validate={(values) => {
            setIsValidForm(formValidator.isValidSync(values))
            setInitialValues(values)
            return {}
          }}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit} autoComplete="off">
              {editInsurance.startDate && editInsurance.policyName && editInsurance.providerName && (
                <>
                  <Text color={'gray'}>
                    Please attest that you have qualifying individual health insurance coverage for the selected month.
                  </Text>

                  <DefaultRadioGroupV2
                    label={
                      <>
                        Are you covered by {editInsurance.providerName} {editInsurance.policyName} for{' '}
                        {formatShortMonthYear(editInsurance.startDate)}?
                      </>
                    }
                    name={'coveredByAnIndividualHealthInsurancePolicy'}
                    direction={'horizontal'}
                    items={[
                      { name: 'Yes', value: true },
                      { name: 'No', value: false },
                    ]}
                  ></DefaultRadioGroupV2>
                </>
              )}

              {formik.values.coveredByAnIndividualHealthInsurancePolicy !== undefined &&
                !formik.values.coveredByAnIndividualHealthInsurancePolicy && (
                  <>
                    <DefaultDatePickerV2
                      name={'endDate'}
                      picker={'month'}
                      monthValue={'lastDayOfMonth'}
                      disabledDate={(current) => current && isFutureDate(current)}
                      label={'When did this coverage end?'}
                    />

                    <DefaultRadioGroupV2
                      label={'Are you covered by a different individual health insurance policy?'}
                      name={'coveredByAnotherIndividualHealthInsurancePolicy'}
                      direction={'horizontal'}
                      items={[
                        { name: 'Yes', value: true },
                        { name: 'No', value: false },
                      ]}
                    ></DefaultRadioGroupV2>

                    {!formik.values.coveredByAnIndividualHealthInsurancePolicy &&
                      formik.values.coveredByAnotherIndividualHealthInsurancePolicy !== undefined &&
                      !formik.values.coveredByAnotherIndividualHealthInsurancePolicy && (
                        <Alert
                          showIcon={true}
                          alignItems={'top'}
                          message={
                            <>
                              <p>
                                Per regulation, you are required to have coverage under a qualified individual health
                                insurance policy in order to participate in your ICHRA.
                              </p>
                              <p>
                                You will not be eligible to receive reimbursements for any expenses incurred in{' '}
                                {formatShortMonthYear(editInsurance.startDate)}.
                              </p>
                            </>
                          }
                        />
                      )}

                    {formik.values.coveredByAnotherIndividualHealthInsurancePolicy !== undefined &&
                      formik.values.coveredByAnotherIndividualHealthInsurancePolicy && (
                        <>
                          <DefaultInputV2
                            name={'providerName'}
                            label={'Name of the health insurance company'}
                            placeholder={'Stride Health'}
                          />
                          <DefaultInputV2
                            name={'policyName'}
                            label={'Name of health insurance plan'}
                            placeholder={'Silver PPO'}
                          />
                          <DefaultDatePickerV2
                            picker={'month'}
                            name={'providerStartDate'}
                            monthValue={'firstDayOfMonth'}
                            disabledDate={(current) => current && isFutureDate(current)}
                            label={'Select your coverage start month'}
                          />
                        </>
                      )}
                  </>
                )}
            </form>
          )}
        </Formik>
      </Modal>
    </>
  )
}

const formValidator = Yup.object({
  endDate: Yup.string()
    .nullable()
    .when('coveredByAnIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => !coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
  coveredByAnotherIndividualHealthInsurancePolicy: Yup.boolean()
    .nullable()
    .when('coveredByAnIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => !coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.boolean().nullable().required('Required'),
    }),
  providerName: Yup.string()
    .nullable()
    .when('coveredByAnotherIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
  policyName: Yup.string()
    .nullable()
    .when('coveredByAnotherIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
  providerStartDate: Yup.string()
    .nullable()
    .when('coveredByAnotherIndividualHealthInsurancePolicy', {
      is: (coveredByAnIndividualHealthInsurancePolicy: boolean) => coveredByAnIndividualHealthInsurancePolicy,
      then: Yup.string().nullable().required('Required'),
    }),
})

export default EditInsuranceCoverageModal
